import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { toast } from 'sonner';
import { Link2 } from 'lucide-react';
import { useUser } from '../context/UserContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TextNoteDialog = ({ open, onOpenChange, note, onSuccess, preselectedActivityId = null }) => {
  const { currentUser } = useUser();
  const [companies, setCompanies] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [opportunities, setOpportunities] = useState([]);
  const [activities, setActivities] = useState([]);
  const [activityMode, setActivityMode] = useState('new');
  const [formData, setFormData] = useState({
    title: '',
    date: '',
    type: 'meeting',
    activity_id: '',
    company_id: '',
    contact_id: '',
    opportunity_id: '',
    creator: '',
    attendees: '',
    raw_notes: '',
    summary: '',
    tags: '',
    typsa_category: '',
    typsa_category_value: ''
  });
  const [loading, setLoading] = useState(false);

  // Filter opportunities by selected company
  const filteredOpportunities = formData.company_id 
    ? opportunities.filter(opp => opp.company_id === formData.company_id)
    : opportunities;

  // Filter contacts by selected company
  const filteredContacts = formData.company_id
    ? contacts.filter(c => c.company_id === formData.company_id)
    : contacts;

  // Check if selected company is TYPSA
  const selectedCompany = companies.find(c => c.id === formData.company_id);
  const isTypsaCompany = selectedCompany?.name?.toUpperCase().includes('TYPSA');

  useEffect(() => {
    if (open) {
      fetchRelatedData();
    }
  }, [open]);

  useEffect(() => {
    if (note) {
      setFormData({
        title: note.title || '',
        date: note.date || '',
        type: note.type || 'meeting',
        activity_id: note.activity_id || '',
        company_id: note.company_id || '',
        contact_id: note.contact_id || '',
        opportunity_id: note.opportunity_id || '',
        creator: note.creator || '',
        attendees: note.attendees || '',
        raw_notes: note.raw_notes || '',
        summary: note.summary || '',
        tags: note.tags ? note.tags.join(', ') : '',
        typsa_category: note.typsa_category || '',
        typsa_category_value: note.typsa_category_value || ''
      });
      setActivityMode(note.activity_id ? 'existing' : 'new');
    } else {
      const today = new Date().toISOString().split('T')[0];
      setFormData({
        title: '',
        date: today,
        type: 'meeting',
        activity_id: preselectedActivityId || '',
        company_id: '',
        contact_id: '',
        opportunity_id: '',
        creator: currentUser?.name || '',  // Auto-fill with current user
        attendees: '',
        raw_notes: '',
        summary: '',
        tags: '',
        typsa_category: '',
        typsa_category_value: ''
      });
      setActivityMode(preselectedActivityId ? 'existing' : 'new');
    }
  }, [note, open, preselectedActivityId, currentUser]);

  useEffect(() => {
    if (activityMode === 'existing' && formData.activity_id) {
      const selectedActivity = activities.find(a => a.id === formData.activity_id);
      if (selectedActivity) {
        setFormData(prev => ({
          ...prev,
          date: selectedActivity.date || prev.date,
          type: selectedActivity.type || prev.type,
          company_id: selectedActivity.company_id || prev.company_id,
          contact_id: selectedActivity.contact_id || prev.contact_id,
          opportunity_id: selectedActivity.opportunity_id || prev.opportunity_id,
          raw_notes: selectedActivity.notes || prev.raw_notes
        }));
      }
    }
  }, [formData.activity_id, activityMode, activities]);

  // Reset opportunity and contact when company changes
  const handleCompanyChange = (value) => {
    const newCompanyId = value === "none" ? "" : value;
    setFormData(prev => ({
      ...prev,
      company_id: newCompanyId,
      opportunity_id: '',  // Reset opportunity
      contact_id: '',  // Reset contact
      typsa_category: '',  // Reset TYPSA fields
      typsa_category_value: ''
    }));
  };

  // Handle contact change - auto-fill TYPSA category if applicable
  const handleContactChange = (value) => {
    const newContactId = value === "none" ? "" : value;
    let newTypsaCategory = '';
    let newTypsaCategoryValue = '';
    
    if (isTypsaCompany && newContactId) {
      const contact = contacts.find(c => c.id === newContactId);
      if (contact?.typsa_category && contact?.typsa_category_value) {
        newTypsaCategory = contact.typsa_category;
        newTypsaCategoryValue = contact.typsa_category_value;
      }
    }
    
    setFormData(prev => ({
      ...prev,
      contact_id: newContactId,
      typsa_category: newTypsaCategory,
      typsa_category_value: newTypsaCategoryValue
    }));
  };

  const fetchRelatedData = async () => {
    try {
      const [companiesRes, contactsRes, oppsRes, activitiesRes] = await Promise.all([
        axios.get(`${API}/companies`),
        axios.get(`${API}/contacts`),
        axios.get(`${API}/opportunities`),
        axios.get(`${API}/activities`)
      ]);
      setCompanies(companiesRes.data);
      setContacts(contactsRes.data);
      setOpportunities(oppsRes.data);
      const availableActivities = activitiesRes.data.filter(a => !a.meeting_note_id);
      setActivities(availableActivities);
    } catch (error) {
      console.error('Error fetching related data:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim() || !formData.date) {
      toast.error('El título y la fecha son obligatorios');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...formData,
        activity_id: activityMode === 'existing' ? formData.activity_id : null,
        tags: formData.tags ? formData.tags.split(',').map(t => t.trim()).filter(Boolean) : []
      };

      if (note) {
        await axios.put(`${API}/text-notes/${note.id}`, payload);
        toast.success('Nota de texto actualizada');
      } else {
        await axios.post(`${API}/text-notes`, payload);
        toast.success(activityMode === 'new' 
          ? 'Nota de texto creada con nueva actividad' 
          : 'Nota de texto vinculada a actividad existente');
      }
      onSuccess();
      onOpenChange(false);
    } catch (error) {
      console.error('Error saving text note:', error);
      toast.error('Error al guardar la nota de texto');
    } finally {
      setLoading(false);
    }
  };

  const getActivityLabel = (activity) => {
    const typeLabels = { meeting: 'Reunión', call: 'Llamada', email: 'Email' };
    const type = typeLabels[activity.type] || activity.type;
    return `${type} - ${activity.date}${activity.notes ? ` (${activity.notes.substring(0, 30)}...)` : ''}`;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="text-note-dialog">
        <DialogHeader>
          <DialogTitle>{note ? 'Editar Nota de Texto' : 'Nueva Nota de Texto'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4">
            {!note && (
              <div className="p-4 bg-muted/50 rounded-lg space-y-3">
                <Label className="text-sm font-medium flex items-center gap-2">
                  <Link2 className="w-4 h-4" />
                  Vincular a Actividad
                </Label>
                <RadioGroup 
                  value={activityMode} 
                  onValueChange={setActivityMode}
                  className="flex gap-4"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="new" id="new-activity" />
                    <Label htmlFor="new-activity" className="font-normal cursor-pointer">
                      Crear nueva actividad
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="existing" id="existing-activity" />
                    <Label htmlFor="existing-activity" className="font-normal cursor-pointer">
                      Vincular a actividad existente
                    </Label>
                  </div>
                </RadioGroup>
                
                {activityMode === 'existing' && (
                  <Select
                    value={formData.activity_id || "none"}
                    onValueChange={(value) => setFormData({ ...formData, activity_id: value === "none" ? "" : value })}
                  >
                    <SelectTrigger data-testid="note-activity-select">
                      <SelectValue placeholder="Seleccionar actividad..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">-- Seleccionar --</SelectItem>
                      {activities.length === 0 ? (
                        <SelectItem value="no-activities" disabled>No hay actividades disponibles</SelectItem>
                      ) : (
                        activities.map((activity) => (
                          <SelectItem key={activity.id} value={activity.id}>
                            {getActivityLabel(activity)}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                )}
                
                {activityMode === 'new' && (
                  <p className="text-xs text-muted-foreground">
                    Se creará automáticamente una actividad con los datos de esta nota.
                  </p>
                )}
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="title">Título *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  data-testid="note-title-input"
                  required
                />
              </div>
              <div>
                <Label htmlFor="date">Fecha *</Label>
                <Input
                  id="date"
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  data-testid="note-date-input"
                  required
                  disabled={activityMode === 'existing' && formData.activity_id}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="type">Tipo</Label>
                <Select 
                  value={formData.type} 
                  onValueChange={(value) => setFormData({ ...formData, type: value })}
                >
                  <SelectTrigger data-testid="note-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="meeting">Reunión</SelectItem>
                    <SelectItem value="call">Llamada</SelectItem>
                    <SelectItem value="reflexion">Reflexión</SelectItem>
                    <SelectItem value="evento">Evento</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="company">Empresa</Label>
                <Select
                  value={formData.company_id || "none"}
                  onValueChange={handleCompanyChange}
                >
                  <SelectTrigger data-testid="note-company-select">
                    <SelectValue placeholder="Seleccionar empresa" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Ninguna</SelectItem>
                    {companies.map((company) => (
                      <SelectItem key={company.id} value={company.id}>
                        {company.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="contact">Contacto</Label>
                <Select
                  value={formData.contact_id || "none"}
                  onValueChange={handleContactChange}
                >
                  <SelectTrigger data-testid="note-contact-select">
                    <SelectValue placeholder="Seleccionar contacto" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Ninguno</SelectItem>
                    {filteredContacts.map((contact) => (
                      <SelectItem key={contact.id} value={contact.id}>
                        {contact.first_name || contact.name} {contact.last_name || ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* TYPSA Category - shown when TYPSA is selected and contact has category */}
            {isTypsaCompany && formData.typsa_category && (
              <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                <Label className="text-blue-700 dark:text-blue-300 text-xs uppercase tracking-wider">
                  Clasificación TYPSA (del contacto)
                </Label>
                <div className="mt-1 font-medium text-blue-800 dark:text-blue-200">
                  {formData.typsa_category === 'division' && 'División'}
                  {formData.typsa_category === 'departamento' && 'Departamento'}
                  {formData.typsa_category === 'territorio' && 'Territorio'}
                  {formData.typsa_category === 'grupo' && 'Grupo'}
                  : {formData.typsa_category_value}
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="opportunity">Oportunidad</Label>
                <Select
                  value={formData.opportunity_id || "none"}
                  onValueChange={(value) => setFormData({ ...formData, opportunity_id: value === "none" ? "" : value })}
                >
                  <SelectTrigger data-testid="note-opportunity-select">
                    <SelectValue placeholder="Seleccionar oportunidad" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Ninguna</SelectItem>
                    {filteredOpportunities.map((opp) => (
                      <SelectItem key={opp.id} value={opp.id}>
                        {opp.title || opp.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="creator">Creador</Label>
                <Input
                  id="creator"
                  value={formData.creator}
                  onChange={(e) => setFormData({ ...formData, creator: e.target.value })}
                  placeholder="Nombre del creador"
                  data-testid="note-creator-input"
                />
              </div>
              <div>
                <Label htmlFor="tags">Etiquetas (separadas por coma)</Label>
                <Input
                  id="tags"
                  value={formData.tags}
                  onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                  placeholder="ej: estrategia, q1-planning"
                  data-testid="note-tags-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="attendees">Asistentes</Label>
              <Textarea
                id="attendees"
                value={formData.attendees}
                onChange={(e) => setFormData({ ...formData, attendees: e.target.value })}
                rows={2}
                placeholder="Lista de asistentes"
                data-testid="note-attendees-input"
              />
            </div>

            <div>
              <Label htmlFor="raw_notes">Notas</Label>
              <Textarea
                id="raw_notes"
                value={formData.raw_notes}
                onChange={(e) => setFormData({ ...formData, raw_notes: e.target.value })}
                rows={6}
                placeholder="Notas de la reunión..."
                data-testid="note-raw-notes-input"
              />
            </div>

            <div>
              <Label htmlFor="summary">Resumen</Label>
              <Textarea
                id="summary"
                value={formData.summary}
                onChange={(e) => setFormData({ ...formData, summary: e.target.value })}
                rows={4}
                placeholder="Puntos clave y acciones a seguir..."
                data-testid="note-summary-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="cancel-note-btn">
              Cancelar
            </Button>
            <Button type="submit" disabled={loading} data-testid="save-note-btn">
              {loading ? 'Guardando...' : 'Guardar'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default TextNoteDialog;
